// npm i jspdf jspdf-autotable
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosDash";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STORE_STORAGE_KEY = "selectedStore"; // import yours if it already exists
const ALL_STORES = "All Stores";
const API_URL = "/fetch-empire-report-Data";
const PAGE_SIZES = [10, 25, 50];
const LEFT_LOGO = "/logo.png";
const RIGHT_LOGO = "/empirelogo.webp";
const MIN = 60000;
const BLOCK = 15; // minutes per timeline block (on screen only)
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const pad = (n) => String(n).padStart(2, "0");

// The business day runs until 02:30, so before 02:30 "today" is still yesterday's date.
const todayStr = () => {
  const d = new Date(Date.now() - 2.5 * 60 * 60 * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// "2026-10-29" -> "29 Oct 2026"
const prettyDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// postgresCreatedAt already holds local clock time inside the UTC value,
// so we read it with UTC getters (no +5:30 shifting).
const fmtTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return "-";
  let h = d.getUTCHours();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${pad(h)}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} ${ap}`;
};

const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return "-";
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${fmtTime(v)}`;
};

// 24 hour "HH:MM"
const fmtHM = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const d = new Date(v);
  if (isNaN(d)) return "-";
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

// "30 Oct 11:00"
const fmtDM = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return "-";
  return `${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${fmtHM(v)}`;
};

const fmtDuration = (sec) => {
  if (sec === undefined || sec === null || isNaN(sec)) return "-";
  const s = Math.round(Number(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m ${r}s`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
};

// minutes -> "2h 5m"
const minsText = (m) => {
  const n = Math.round(Number(m) || 0);
  if (!n) return "0m";
  return n >= 60 ? `${Math.floor(n / 60)}h ${n % 60}m` : `${n}m`;
};

// gloves / hairnet: YES -> "Worn", NO -> "Missing"
// (hairnet "UNKNOWN" is treated as worn)
const wornText = (raw, unknownAsWorn = false) => {
  const s = String(raw || "").trim().toUpperCase();
  if (!s) return "-";
  if (s === "YES" || (unknownAsWorn && s === "UNKNOWN")) return "Worn";
  if (s === "NO") return "Missing";
  return nice(s);
};
const glovesOf = (r) => wornText(r?.gloveStatus);
const hairnetOf = (r) => wornText(r?.hairnetStatus, true);

const nice = (s) =>
  s ? String(s).replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : "-";

const hex2rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const TONE_RGB = { ok: [23, 121, 77], warn: [154, 91, 0], bad: [180, 35, 35] };

// only used to pick a colour (never shown as a number)
const toneOf = (present, absent) => {
  const known = present + absent;
  if (!known) return "none";
  const r = present / known;
  return r >= 0.85 ? "ok" : r >= 0.6 ? "warn" : "bad";
};

// colour of a 15 minute block from its present / absent minutes (on screen only)
const blockTone = (p, a) => {
  const known = p + a;
  if (!known) return "none";
  const r = p / known;
  return r >= 0.8 ? "ok" : r >= 0.4 ? "mid" : "bad";
};

// Loads an image (png / webp) and returns { data: PNG dataURL, w, h } for the PDF.
// Returns null if the image cannot be loaded, so the PDF still downloads.
const loadLogo = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/*
  Every alert use case: key (matches API response), label, accent colours,
  columns (h = header, v = text value, tone = optional badge colour),
  images = list of {label, url} for a row.
*/
const USE_CASES = [
  {
    key: "cashier",
    label: "Cash Drawer Alerts",
    color: "#4f7cff",
    soft: "#eaf0ff",
    columns: [
      { h: "Time", v: (r) => fmtTime(r.postgresCreatedAt) },
      {
        h: "Drawer",
        v: (r) => nice(r.drawerState),
        tone: (r) => (r.drawerState === "OPENED" ? "warn" : "ok"),
      },
      {
        h: "Alert",
        v: (r) => (r.alertGenerated ? "Generated" : "No"),
        tone: (r) => (r.alertGenerated ? "bad" : "ok"),
      },
    ],
    images: (r) => [{ label: "Image", url: r.imagePath }],
  },
  {
    key: "hygiene",
    label: "Hygiene & Safety Alerts",
    color: "#14a38b",
    soft: "#e2f6f1",
    columns: [
  { h: "Time", v: (r) => fmtTime(r.postgresCreatedAt) },
  { h: "Alert type", v: (r) => (isDuster(r) ? "Duster" : nice(r.alertType)) },
  {
    h: "Gloves",
    v: (r) => glovesOf(r),
    tone: (r) => {
      const g = glovesOf(r);
      return g === "Missing" ? "bad" : g === "Worn" ? "ok" : "none";
    },
  },
  {
    h: "Hairnet",
    v: (r) => hairnetOf(r),
    tone: (r) => {
      const h = hairnetOf(r);
      return h === "Missing" ? "bad" : h === "Worn" ? "ok" : "none";
    },
  },
  {
    h: "Duster",
    v: (r) => (isDuster(r) ? "Detected" : "-"),
    tone: (r) => (isDuster(r) ? "bad" : "none"),
  },
],
    images: (r) => [{ label: "Image", url: r.imagePath }],
  },
  {
    key: "kebab",
    label: "Kebab Frying",
    color: "#f08a3c",
    soft: "#fdeedd",
    columns: [
      { h: "Logged at", v: (r) => fmtTime(r.postgresCreatedAt) },
      { h: "Start", v: (r) => fmtTime(r.startTime) },
      { h: "End", v: (r) => fmtTime(r.endTime) },
      { h: "Duration", v: (r) => fmtDuration(r.durationSeconds) },
      {
        h: "Status",
        v: (r) => nice(r.status),
        tone: (r) => (r.status === "completed" ? "ok" : "warn"),
      },
    ],
    images: (r) => [
      { label: "Start", url: r.startImagePath },
      { label: "End", url: r.endImagePath },
    ],
  },
  {
    key: "materialInwards",
    label: "Material Inwards Alerts",
    color: "#8a63d2",
    soft: "#f0eafb",
    columns: [
      { h: "Logged at", v: (r) => fmtTime(r.postgresCreatedAt) },
      { h: "Vehicle no.", v: (r) => r.anprNumber || "-" },
      { h: "Truck arrival", v: (r) => fmtDateTime(r.truckArrivalTimestamp) },
      { h: "Unload start", v: (r) => fmtDateTime(r.unloadingStartTimestamp) },
      { h: "Unload end", v: (r) => fmtDateTime(r.unloadingEndTimestamp) },
      { h: "Duration", v: (r) => fmtDuration(r.unloadingDurationSeconds) },
      {
        h: "Status",
        v: (r) => nice(r.status),
        tone: (r) => (String(r.status).toUpperCase() === "COMPLETED" ? "ok" : "warn"),
      },
    ],
    images: (r) => [{ label: "Image", url: r.imageUrl }],
  },
  {
    key: "storeRoom",
    label: "Store Room Alerts",
    color: "#d9578a",
    soft: "#fbe9f1",
    columns: [
      { h: "Time", v: (r) => fmtTime(r.postgresCreatedAt) },
      { h: "Alert type", v: (r) => nice(r.alertType), tone: () => "warn" },
      { h: "People", v: (r) => String(r.currentPeopleCount ?? "-") },
      { h: "Message", v: (r) => r.alertMessage || "-" },
    ],
    images: (r) => [{ label: "Image", url: r.imagePath }],
  },
  {
    key: "writer",
    label: "Writer Absent Alerts",
    color: "#2a9fd6",
    soft: "#e3f3fb",
    columns: [
      { h: "Time", v: (r) => fmtTime(r.postgresCreatedAt) },
      { h: "Alert type", v: (r) => nice(r.alertType), tone: () => "bad" },
      { h: "Absent checks", v: (r) => String(r.consecutiveAbsentChecks ?? "-") },
    ],
    images: (r) => [{ label: "Snapshot", url: r.snapshotPath }],
  },
  {
    key: "writerFood",
    label: "Writer Food Alerts",
    color: "#c9a227",
    soft: "#fbf4d9",
    columns: [
      { h: "Time", v: (r) => fmtTime(r.postgresCreatedAt) },
      { h: "Food name", v: () => "Food and Beverage" },
      { h: "Clicked at", v: (r) => fmtTime(r.clickedAt) },
    ],
    images: (r) => [{ label: "Image", url: r.imagePath }],
  },
];

// default choices in the download dialog
const makeOpts = (date, store, view, active) => {
  const cases = {};
  USE_CASES.forEach((u) => {
    cases[u.key] = view === "presence" ? false : view === "alerts" ? u.key === active : true;
  });
  return {
    date,
    store,
    presence: view !== "alerts",
    cashier: true,
    writer: true,
    shiftWise: true,
    cases,
    images: true,
    order: "oldest", // oldest | newest
    orientation: "landscape", // landscape | portrait
  };
};

const isDuster = (r) => String(r?.alertType || "").trim().toLowerCase() === "duster";

// Page numbers with ellipsis: [1, "…", 4, 5, 6, "…", 20]
const pageList = (cur, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const start = Math.max(2, cur - 1);
  const end = Math.min(total - 1, cur + 1);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
};

/* -------------------------------------------------------------------------- */
/*  STYLES                                                                     */
/* -------------------------------------------------------------------------- */

const CSS = `
.rp-root{--bg:#f5f8fd;--card:#ffffff;--line:#e3e9f4;--ink:#1f2a44;--muted:#6b7794;--brand:#3d63dd;--brand-soft:#eaf0ff;
  --green:#34c38f;--amber:#f6b93b;--red:#f1736f;
  font-family:inherit;background:var(--bg);color:var(--ink);
  min-height:100vh;box-sizing:border-box;padding:24px 0 40px}
.rp-root *{box-sizing:border-box}
.rp-wrap{max-width:1280px;margin:0 auto;padding:0 24px}

/* ---------- toolbar ---------- */
.rp-bar{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;background:var(--card);border:1px solid var(--line);
  border-radius:16px;padding:12px 16px;margin-bottom:16px;box-shadow:0 1px 2px rgba(31,42,68,.04)}
.rp-bar .sum{display:flex;flex-direction:column}
.rp-bar .sum b{font-size:18px;font-weight:800;letter-spacing:-.01em}
.rp-bar .sum span{font-size:13px;color:var(--muted)}
.rp-actions{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.rp-field{display:flex;align-items:center;gap:8px;background:#f8faff;border:1px solid var(--line);border-radius:10px;padding:0 12px;height:40px}
.rp-field label{font-size:12.5px;color:var(--muted);font-weight:600}
.rp-field input,.rp-field select{border:none;outline:none;background:transparent;font:inherit;font-size:14px;color:var(--ink)}
.rp-btn{border:1px solid var(--line);background:#fff;color:var(--ink);font:inherit;font-size:14px;font-weight:600;
  height:40px;padding:0 16px;border-radius:10px;cursor:pointer;transition:background .15s,border-color .15s;display:inline-flex;align-items:center;gap:8px}
.rp-btn:hover:not(:disabled){background:#eef2fb;border-color:#d3dcf0}
.rp-btn.primary{background:var(--brand);border-color:var(--brand);color:#fff}
.rp-btn.primary:hover:not(:disabled){background:#2f50c2}
.rp-btn.sm{height:34px;padding:0 12px;font-size:13px}
.rp-btn:disabled{opacity:.55;cursor:not-allowed}
.rp-btn:focus-visible,.rp-uc:focus-visible,.rp-thumb:focus-visible,.rp-pg:focus-visible,.rp-field input:focus-visible,.rp-search input:focus-visible,.rp-seg button:focus-visible,.rp-chip:focus-visible{outline:2px solid var(--brand);outline-offset:2px}

/* ---------- view switch ---------- */
.rp-seg{display:inline-flex;background:#e6edfc;border-radius:12px;padding:4px;gap:4px;margin-bottom:16px;flex-wrap:wrap}
.rp-seg button{border:none;background:transparent;font:inherit;font-weight:700;font-size:14px;padding:9px 18px;border-radius:9px;cursor:pointer;color:var(--muted)}
.rp-seg button.on{background:#fff;color:var(--brand);box-shadow:0 1px 3px rgba(31,42,68,.12)}

/* ---------- use case cards ---------- */
.rp-ucs{display:grid;grid-template-columns:repeat(auto-fit,minmax(165px,1fr));gap:12px;margin-bottom:20px}
.rp-uc{display:flex;flex-direction:column;gap:10px;text-align:left;font:inherit;color:var(--ink);cursor:pointer;position:relative;overflow:hidden;
  background:var(--card);border:1.5px solid var(--line);border-radius:14px;padding:14px 14px 12px;transition:border-color .15s,background .15s,box-shadow .15s}
.rp-uc::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--c)}
.rp-uc:hover{border-color:var(--c)}
.rp-uc .nm{font-size:13px;font-weight:600;color:var(--muted);padding-left:6px}
.rp-uc .ct{font-size:26px;font-weight:800;letter-spacing:-.02em;padding-left:6px;line-height:1}
.rp-uc.active{background:var(--s);border-color:var(--c);box-shadow:0 4px 14px rgba(31,42,68,.08)}
.rp-uc.active .nm{color:var(--ink)}

/* ---------- panel ---------- */
.rp-panel{background:var(--card);border:1px solid var(--line);border-radius:18px;overflow:hidden;box-shadow:0 1px 2px rgba(31,42,68,.04);margin-bottom:18px}
.rp-panel-head{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;padding:16px 20px;background:var(--s);border-bottom:1px solid var(--line)}
.rp-panel-head h2{margin:0;font-size:18px;font-weight:800;display:flex;align-items:center;gap:10px}
.rp-panel-head h2 i{width:12px;height:12px;border-radius:4px;background:var(--c);display:inline-block}
.rp-panel-head small{display:block;margin-top:3px;color:var(--muted);font-size:13px;font-weight:500}
.rp-tools{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.rp-search input{height:34px;width:230px;max-width:100%;border:1px solid var(--line);border-radius:10px;padding:0 12px;font:inherit;font-size:13.5px;color:var(--ink);background:#fff}
.rp-search input:focus{border-color:var(--brand)}
.rp-tablewrap{overflow-x:auto}
.rp-table{width:100%;border-collapse:collapse;font-size:13.5px}
.rp-table th{text-align:left;font-weight:700;color:var(--muted);font-size:12.5px;padding:12px 16px;background:#fafbff;border-bottom:1px solid var(--line);white-space:nowrap}
.rp-table td{padding:12px 16px;border-bottom:1px solid #eef1f8;vertical-align:middle}
.rp-table tbody tr:nth-child(even){background:#fbfcff}
.rp-table tbody tr:hover{background:var(--s)}
.rp-table td.idx{color:var(--muted);width:48px;font-weight:600}
.rp-table tr.click{cursor:pointer}
.rp-badge{display:inline-block;padding:3px 10px;border-radius:999px;font-size:12px;font-weight:700;white-space:nowrap}
.rp-badge.ok{background:#e2f6ec;color:#17794d}
.rp-badge.warn{background:#fff1da;color:#9a5b00}
.rp-badge.bad{background:#fde6e6;color:#b42323}
.rp-badge.none{background:#eef1f8;color:var(--muted)}
.rp-thumbs{display:flex;gap:8px}
.rp-thumb{border:1px solid var(--line);background:#f3f5fa;border-radius:8px;padding:0;width:62px;height:44px;overflow:hidden;cursor:pointer;position:relative}
.rp-thumb:hover{border-color:var(--brand)}
.rp-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.rp-thumb span{position:absolute;left:0;right:0;bottom:0;background:rgba(255,255,255,.92);font-size:9.5px;text-align:center;color:var(--ink);font-weight:700}
.rp-empty{padding:48px 20px;text-align:center;color:var(--muted);font-size:14px}

/* ---------- pagination ---------- */
.rp-foot{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;padding:12px 20px;border-top:1px solid var(--line);background:#fafbff}
.rp-foot .info{font-size:13px;color:var(--muted)}
.rp-foot .right{display:flex;flex-wrap:wrap;gap:12px;align-items:center}
.rp-size{display:flex;align-items:center;gap:6px;font-size:13px;color:var(--muted)}
.rp-size select{border:1px solid var(--line);border-radius:8px;height:32px;padding:0 6px;font:inherit;font-size:13px;color:var(--ink);background:#fff}
.rp-pages{display:flex;gap:4px;align-items:center}
.rp-pg{min-width:34px;height:34px;border:1px solid var(--line);background:#fff;border-radius:8px;font:inherit;font-size:13px;font-weight:600;color:var(--ink);cursor:pointer;padding:0 8px}
.rp-pg:hover:not(:disabled):not(.on){background:#eef2fb}
.rp-pg.on{background:var(--brand);border-color:var(--brand);color:#fff}
.rp-pg:disabled{opacity:.45;cursor:not-allowed}
.rp-dots{padding:0 4px;color:var(--muted)}

/* ---------- presence ---------- */
.rp-note{display:flex;flex-wrap:wrap;gap:8px 18px;align-items:center;background:#fff;border:1px solid var(--line);border-radius:14px;padding:10px 16px;margin-bottom:16px;font-size:13px;color:var(--muted)}
.rp-note b{color:var(--ink)}
.rp-chips{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px}
.rp-chip{border:1.5px solid var(--line);background:#fff;font:inherit;font-weight:700;font-size:13.5px;color:var(--muted);padding:8px 16px;border-radius:999px;cursor:pointer}
.rp-chip.on{background:var(--brand);border-color:var(--brand);color:#fff}
.rp-roles{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,480px),1fr));gap:16px;align-items:start}
.rp-card{background:var(--card);border:1px solid var(--line);border-radius:18px;overflow:hidden;box-shadow:0 1px 2px rgba(31,42,68,.04)}
.rp-card-head{display:flex;flex-wrap:wrap;gap:10px;justify-content:space-between;align-items:center;padding:14px 18px;background:var(--s);border-bottom:1px solid var(--line)}
.rp-card-head h3{margin:0;font-size:17px;font-weight:800;display:flex;align-items:center;gap:10px}
.rp-card-head h3 i{width:12px;height:12px;border-radius:4px;background:var(--c);display:inline-block}
.rp-card-body{padding:18px;display:flex;flex-direction:column;gap:18px}
.rp-sub{font-size:12.5px;font-weight:700;color:var(--muted);margin:0 0 8px;display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap}
.rp-split{height:10px;border-radius:999px;background:var(--red);overflow:hidden}
.rp-split div{height:100%;background:var(--green)}
.rp-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:8px}
.rp-stat{background:#f8faff;border:1px solid #edf0f8;border-radius:10px;padding:8px 10px;min-width:0}
.rp-stat b{display:block;font-size:16px;font-weight:800;white-space:nowrap}
.rp-stat span{font-size:11.5px;color:var(--muted)}
.rp-stat.ok b{color:#17794d}.rp-stat.bad b{color:#b42323}.rp-stat.warn b{color:#9a5b00}
.rp-shifts{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,210px),1fr));gap:12px}
.rp-shift{border:1px solid var(--line);border-radius:14px;padding:12px;background:#fcfdff}
.rp-shift-head{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin-bottom:10px}
.rp-shift-head b{font-size:14px;display:block}
.rp-shift-head small{font-size:12px;color:var(--muted)}
.rp-mini{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:10px}
.rp-mini div{background:#f3f6fc;border-radius:8px;padding:6px 8px;min-width:0}
.rp-mini b{display:block;font-size:13px;font-weight:800;white-space:nowrap}
.rp-mini span{font-size:10.5px;color:var(--muted)}
.rp-tl{display:flex;gap:2px;margin-top:6px}
.rp-tl i{flex:1;height:22px;border-radius:4px;background:#e2e8f0;min-width:0}
.rp-tl i.ok{background:var(--green)}.rp-tl i.mid{background:var(--amber)}.rp-tl i.bad{background:var(--red)}
.rp-ax{display:flex;justify-content:space-between;font-size:10.5px;color:var(--muted);margin-top:4px}
.rp-legend{display:flex;flex-wrap:wrap;gap:12px;font-size:11.5px;color:var(--muted)}
.rp-legend span{display:inline-flex;align-items:center;gap:5px}
.rp-legend i{width:10px;height:10px;border-radius:3px;display:inline-block}
.rp-tag{font-size:10.5px;font-weight:700;background:#fff1da;color:#9a5b00;border-radius:999px;padding:2px 8px;white-space:nowrap}
.rp-tl-empty{font-size:12px;color:var(--muted);padding:6px 0}

/* ---------- states ---------- */
.rp-state{background:var(--card);border:1px dashed #cdd6ea;border-radius:16px;padding:56px 20px;text-align:center;color:var(--muted)}
.rp-error{background:#fde6e6;border:1px solid #f4c3c3;color:#a12626;border-radius:12px;padding:12px 16px;margin-bottom:16px;font-size:14px;display:flex;justify-content:space-between;gap:12px;align-items:center}
.rp-spin{width:26px;height:26px;border:3px solid #dbe3f5;border-top-color:var(--brand);border-radius:50%;animation:rpspin .8s linear infinite;margin:0 auto 12px}
@keyframes rpspin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.rp-spin{animation-duration:2s}}

/* ---------- modals ---------- */
.rp-overlay{position:fixed;inset:0;background:rgba(31,42,68,.4);display:flex;align-items:center;justify-content:center;padding:16px;z-index:50}
.rp-modal{background:#fff;border-radius:18px;width:100%;max-width:440px;padding:22px;box-shadow:0 20px 50px rgba(31,42,68,.2)}
.rp-modal h3{margin:0 0 4px;font-size:18px;font-weight:800}
.rp-modal p{margin:0 0 18px;color:var(--muted);font-size:13.5px}
.rp-row{display:flex;flex-direction:column;gap:6px;margin-bottom:14px}
.rp-row label{font-size:13px;font-weight:700}
.rp-row input,.rp-row select{border:1px solid var(--line);border-radius:10px;padding:10px 12px;font:inherit;font-size:14px;color:var(--ink);background:#fff;outline:none}
.rp-row input:focus,.rp-row select:focus{border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-soft)}
.rp-modal-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px}
.rp-preview{max-width:900px;padding:14px}
.rp-preview img{width:100%;max-height:75vh;object-fit:contain;border-radius:10px;background:#f3f5fa;display:block}
.rp-preview .bar{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;font-weight:700;flex-wrap:wrap}
.rp-preview .nav{display:flex;gap:8px;align-items:center}

/* ---------- download dialog ---------- */
.rp-dl{max-width:620px;max-height:92vh;overflow-y:auto}
.rp-two{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media (max-width:520px){.rp-two{grid-template-columns:1fr}}
.rp-presets{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px}
.rp-group{border:1px solid var(--line);border-radius:14px;padding:12px 14px;margin-bottom:14px;background:#fcfdff}
.rp-group h4{margin:0 0 8px;font-size:13px;font-weight:800;display:flex;justify-content:space-between;align-items:center;gap:8px}
.rp-checks{display:grid;grid-template-columns:1fr 1fr;gap:6px 12px}
@media (max-width:520px){.rp-checks{grid-template-columns:1fr}}
.rp-check{display:flex;align-items:center;gap:8px;font-size:13.5px;cursor:pointer;padding:4px 0}
.rp-check input{width:16px;height:16px;accent-color:var(--brand);cursor:pointer;margin:0}
.rp-check i{width:10px;height:10px;border-radius:3px;display:inline-block;flex:none}
.rp-check.sub{margin-left:22px}
.rp-check.off{opacity:.5}
.rp-sum{background:var(--brand-soft);border-radius:12px;padding:10px 14px;font-size:13px;color:var(--ink);margin-bottom:4px}

@media (max-width:640px){
  .rp-wrap{padding:0 14px}
  .rp-search input{width:100%}
  .rp-search{flex:1}
  .rp-foot{justify-content:center}
}
`;

/* -------------------------------------------------------------------------- */
/*  PRESENCE COMPONENTS (on screen)                                            */
/* -------------------------------------------------------------------------- */

const Stat = ({ value, label, tone = "" }) => (
  <div className={`rp-stat ${tone}`}>
    <b>{value}</b>
    <span>{label}</span>
  </div>
);

const Legend = () => (
  <div className="rp-legend">
    <span><i style={{ background: "var(--green)" }} />Present</span>
    <span><i style={{ background: "var(--amber)" }} />Mixed</span>
    <span><i style={{ background: "var(--red)" }} />Mostly absent</span>
    <span><i style={{ background: "#e2e8f0" }} />No data</span>
  </div>
);

// one coloured block per 15 minutes
const Timeline = ({ shift }) => {
  if (!shift.timeline) return <div className="rp-tl-empty">This shift is outside the business day.</div>;

  const startTs = new Date(shift.from).getTime();
  const tl = shift.timeline;

  const blocks = [];
  for (let i = 0; i < tl.length; i += BLOCK) {
    const seg = tl.slice(i, i + BLOCK);
    let p = 0;
    let a = 0;
    for (const c of seg) {
      if (c === "P") p++;
      else if (c === "A") a++;
    }
    blocks.push({ i, p, a, len: seg.length });
  }

  return (
    <>
      <div className="rp-tl">
        {blocks.map((b) => (
          <i
            key={b.i}
            className={blockTone(b.p, b.a)}
            title={`${fmtHM(startTs + b.i * MIN)} - ${fmtHM(startTs + (b.i + b.len - 1) * MIN)} | ${minsText(b.p)} present, ${minsText(b.a)} absent`}
          />
        ))}
      </div>
      <div className="rp-ax">
        <span>{fmtHM(shift.from)}</span>
        <span>{fmtHM(shift.to)}</span>
      </div>
    </>
  );
};

const ShiftCard = ({ shift }) => {
  const has = shift.known > 0;

  return (
    <div className="rp-shift">
      <div className="rp-shift-head">
        <div>
          <b>{shift.label}</b>
          <small>
            {shift.start} - {shift.end}
          </small>
        </div>
        {has ? (
          <span className={`rp-badge ${toneOf(shift.present, shift.absent)}`}>{minsText(shift.present)} present</span>
        ) : (
          <span className="rp-badge none">No data</span>
        )}
      </div>

      {shift.source === "default" && (
        <div style={{ marginBottom: 8 }}>
          <span className="rp-tag">Default timing</span>
        </div>
      )}

      {has && (
        <div className="rp-mini">
          <div><b style={{ color: "#17794d" }}>{minsText(shift.present)}</b><span>Present</span></div>
          <div><b style={{ color: "#b42323" }}>{minsText(shift.absent)}</b><span>Absent</span></div>
          <div><b>{minsText(shift.longestAbsent)}</b><span>Longest absence</span></div>
        </div>
      )}

      <Timeline shift={shift} />
    </div>
  );
};

const RoleCard = ({ title, color, soft, role }) => {
  const day = role?.day;
  const has = !!role?.hasData;
  const known = day?.known || 0;
  const presentWidth = known ? (day.present / known) * 100 : 0; // bar only, not shown as text

  return (
    <section className="rp-card" style={{ "--c": color, "--s": soft }}>
      <div className="rp-card-head">
        <h3>
          <i />
          {title}
        </h3>
        {!has && <span className="rp-badge none">No data</span>}
      </div>

      {!has ? (
        <div className="rp-empty">No {title.toLowerCase()} presence data for this business day.</div>
      ) : (
        <div className="rp-card-body">
          {/* whole day */}
          <div>
            <div className="rp-sub">
              <span>Whole day</span>
            </div>
            <div className="rp-split" style={{ marginBottom: 12 }}>
              <div style={{ width: `${presentWidth}%` }} />
            </div>
            <div className="rp-stats">
              <Stat value={minsText(day.present)} label="Present" tone="ok" />
              <Stat value={minsText(day.absent)} label="Absent" tone="bad" />
              <Stat value={minsText(day.longestAbsent)} label="Longest absence" tone="warn" />
              <Stat value={fmtHM(day.firstSeen)} label="First present" />
              <Stat value={fmtHM(day.lastSeen)} label="Last present" />
            </div>
          </div>

          {/* shift wise */}
          <div>
            <div className="rp-sub">
              <span>Shift wise</span>
            </div>
            <div className="rp-shifts">
              {(role.shifts || []).map((s) => (
                <ShiftCard key={s.key} shift={s} />
              ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <Legend />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const PresenceView = ({ presence, date, selected, onSelect, loading }) => {
  if (loading && !presence) return null;

  if (!presence) {
    return (
      <div className="rp-state">
        Presence data could not be loaded for {prettyDate(date)}. Press Refresh to try again.
      </div>
    );
  }

  const stores = presence.stores || [];
  if (!stores.length) return <div className="rp-state">No stores found for this date.</div>;

  const active = stores.find((s) => s.store === selected) || stores[0];
  const usesDefault = [active.cashier, active.writer].some((r) => r?.shiftSource === "default");

  const presentText = (r) => (r?.day?.known ? minsText(r.day.present) : "–");
  const absentText = (r) => (r?.day?.known ? minsText(r.day.absent) : "–");

  return (
    <>
      <div className="rp-note">
        <span>
          Business day <b>{fmtDM(presence.window.from)}</b> to <b>{fmtDM(presence.window.to)}</b>
        </span>
        <span>Minutes with no camera data are not counted as absent</span>
        {usesDefault && <span className="rp-tag">Some shift timings are defaults</span>}
      </div>

      {stores.length > 1 && (
        <section className="rp-panel" style={{ "--s": "#eaf0ff", "--c": "#3d63dd" }}>
          <div className="rp-panel-head">
            <div>
              <h2>
                <i />
                Store comparison
              </h2>
              <small>Click a store to see its full presence detail</small>
            </div>
          </div>
          <div className="rp-tablewrap">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Cashier present</th>
                  <th>Cashier absent</th>
                  <th>Writer present</th>
                  <th>Writer absent</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((s) => (
                  <tr key={s.store} className="click" onClick={() => onSelect(s.store)}>
                    <td><b>{s.store}</b></td>
                    <td>{presentText(s.cashier)}</td>
                    <td>{absentText(s.cashier)}</td>
                    <td>{presentText(s.writer)}</td>
                    <td>{absentText(s.writer)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {stores.length > 1 && (
        <div className="rp-chips" role="tablist" aria-label="Choose a store">
          {stores.map((s) => (
            <button
              key={s.store}
              role="tab"
              aria-selected={active.store === s.store}
              className={`rp-chip ${active.store === s.store ? "on" : ""}`}
              onClick={() => onSelect(s.store)}
            >
              {s.store}
            </button>
          ))}
        </div>
      )}

      <div className="rp-roles">
        <RoleCard title="Cashier" color="#4f7cff" soft="#eaf0ff" role={active.cashier} />
        <RoleCard title="Writer" color="#2a9fd6" soft="#e3f3fb" role={active.writer} />
      </div>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  PAGE                                                                       */
/* -------------------------------------------------------------------------- */

const EmpireReport = () => {
  const [storeName, setStoreName] = useState(
    () => localStorage.getItem(STORE_STORAGE_KEY) || ALL_STORES
  );
  const [date, setDate] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [view, setView] = useState("presence"); // "presence" | "alerts"
  const [presenceStore, setPresenceStore] = useState("");

  // one alert use case is shown at a time
  const [active, setActive] = useState(USE_CASES[0].key);
  const [search, setSearch] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // download dialog
  const [pdfOpen, setPdfOpen] = useState(false);
  const [opts, setOpts] = useState(() => makeOpts(todayStr(), ALL_STORES, "presence", USE_CASES[0].key));
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const [preview, setPreview] = useState(null); // { title, items, index }

  const maxDate = todayStr();

  useEffect(() => {
    const updateStoreName = () => {
      setStoreName(localStorage.getItem(STORE_STORAGE_KEY) || ALL_STORES);
    };
    window.addEventListener("storage", updateStoreName);
    window.addEventListener("selectedStoreChanged", updateStoreName);
    return () => {
      window.removeEventListener("storage", updateStoreName);
      window.removeEventListener("selectedStoreChanged", updateStoreName);
    };
  }, []);

  // Esc closes whichever dialog is open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (preview) setPreview(null);
      else if (pdfOpen && !pdfBusy) setPdfOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview, pdfOpen, pdfBusy]);

  const fetchReport = useCallback(
    async (forDate, forStore) => {
      const res = await axios.post(API_URL, { storeName: forStore || storeName, date: forDate });
      const body = res?.data;
      if (!body?.success) throw new Error(body?.message || "Unable to load report");
      return body;
    },
    [storeName]
  );

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError("");
    try {
      setReport(await fetchReport(date));
    } catch (e) {
      setReport(null);
      setError(e?.response?.data?.message || e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [date, fetchReport]);

  // default = today's data on first open; reload when date / store changes
  useEffect(() => {
    load();
  }, [load]);

  // go back to page 1 whenever the list changes
  useEffect(() => {
    setPage(1);
  }, [active, date, storeName, search, pageSize, newestFirst]);

  const data = report?.data || {};
  const counts = useMemo(
    () => USE_CASES.reduce((a, u) => ({ ...a, [u.key]: (data[u.key] || []).length }), {}),
    [data]
  );
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const uc = USE_CASES.find((u) => u.key === active) || USE_CASES[0];
  const allRows = data[uc.key] || [];
  const cols = uc.columns;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = allRows;
    if (q) {
      rows = rows.filter((r) =>
        cols
          .map((c) => String(c.v(r)))
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    const t = (r) => new Date(r.postgresCreatedAt).getTime() || 0;
    return [...rows].sort((a, b) => (newestFirst ? t(b) - t(a) : t(a) - t(b)));
  }, [allRows, cols, search, newestFirst]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const from = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(from, from + pageSize);

  /* --------------------------- download dialog ---------------------------- */
  const storeChoices = useMemo(() => {
    const names = (report?.presence?.stores || []).map((s) => s.store);
    if (storeName !== ALL_STORES) return [storeName];
    return [ALL_STORES, ...names];
  }, [report, storeName]);

  const setOpt = (patch) => setOpts((o) => ({ ...o, ...patch }));
  const setCase = (key, val) => setOpts((o) => ({ ...o, cases: { ...o.cases, [key]: val } }));
  const setAllCases = (val) =>
    setOpts((o) => ({
      ...o,
      cases: USE_CASES.reduce((a, u) => ({ ...a, [u.key]: val }), {}),
    }));

  const preset = (kind) => {
    if (kind === "all") {
      setOpts((o) => ({
        ...o,
        presence: true,
        cashier: true,
        writer: true,
        cases: USE_CASES.reduce((a, u) => ({ ...a, [u.key]: true }), {}),
      }));
    } else if (kind === "presence") {
      setOpts((o) => ({
        ...o,
        presence: true,
        cashier: true,
        writer: true,
        cases: USE_CASES.reduce((a, u) => ({ ...a, [u.key]: false }), {}),
      }));
    } else {
      setOpts((o) => ({
        ...o,
        presence: false,
        cases: USE_CASES.reduce((a, u) => ({ ...a, [u.key]: true }), {}),
      }));
    }
  };

  const openDownload = () => {
    setOpts(makeOpts(date, storeName, view, active));
    setPdfError("");
    setPdfOpen(true);
  };

  const presenceOn = opts.presence && (opts.cashier || opts.writer);
  const selectedCases = USE_CASES.filter((u) => opts.cases[u.key]);
  const nothingSelected = !presenceOn && selectedCases.length === 0;

  /* ------------------------------ PDF download ----------------------------- */
  const downloadPdf = async () => {
    if (!opts.date) {
      setPdfError("Please choose a date");
      return;
    }
    if (nothingSelected) {
      setPdfError("Please choose at least one section to include");
      return;
    }
    setPdfBusy(true);
    setPdfError("");
    try {
      const reuse = opts.date === date && opts.store === storeName && report;
      const [body, leftLogo, rightLogo] = await Promise.all([
        reuse ? Promise.resolve(report) : fetchReport(opts.date, opts.store),
        loadLogo(LEFT_LOGO),
        loadLogo(RIGHT_LOGO),
      ]);
      const rep = body.data || {};
      const pres = body.presence;
      const includePresence = presenceOn && pres?.stores?.length > 0;
      const chosen = selectedCases;
      const withImages = opts.images;

      const doc = new jsPDF({ orientation: opts.orientation, unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const M = 40;
      const INK = [31, 42, 68];
      const MUTED = [107, 119, 148];
      const LINE = [227, 233, 244];
      const totalRecords = chosen.reduce((s, u) => s + (rep[u.key] || []).length, 0);

      /* ---- light header band with both logos ---- */
      const BAND_H = 92;
      doc.setFillColor(238, 244, 255);
      doc.rect(0, 0, pageW, BAND_H, "F");
      doc.setFillColor(246, 241, 255);
      doc.rect(pageW * 0.55, 0, pageW * 0.45, BAND_H, "F");
      doc.setFillColor(61, 99, 221);
      doc.rect(0, BAND_H, pageW, 3, "F");

      const drawLogo = (logo, side) => {
        if (!logo) return;
        const maxH = 46;
        const maxW = 130;
        const ratio = logo.w / logo.h;
        let h = maxH;
        let w = h * ratio;
        if (w > maxW) {
          w = maxW;
          h = w / ratio;
        }
        const x = side === "left" ? M : pageW - M - w;
        doc.addImage(logo.data, "PNG", x, (BAND_H - h) / 2, w, h);
      };
      drawLogo(leftLogo, "left");
      drawLogo(rightLogo, "right");

      const parts = [];
      if (includePresence) {
        parts.push(
          opts.cashier && opts.writer ? "Staff presence" : opts.cashier ? "Cashier presence" : "Writer presence"
        );
      }
      if (chosen.length === USE_CASES.length) parts.push("All alerts");
      else if (chosen.length === 1) parts.push(chosen[0].label);
      else if (chosen.length > 1) parts.push(`${chosen.length} alert types`);
      const caseLabel = parts.join(" + ");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(...INK);
      doc.text("Empire Report", pageW / 2, 40, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...MUTED);
      doc.text(opts.store, pageW / 2, 58, { align: "center" });
      doc.setFontSize(10);
      doc.text(`${prettyDate(opts.date)}  |  ${caseLabel}`, pageW / 2, 74, { align: "center" });

      let cursor = BAND_H + 30;

      const ensureSpace = (need) => {
        if (cursor > pageH - need) {
          doc.addPage();
          cursor = 56;
        }
      };

      // coloured section band
      const band = (label, right, color, soft) => {
        doc.setFillColor(...soft);
        doc.roundedRect(M, cursor, pageW - 2 * M, 28, 5, 5, "F");
        doc.setFillColor(...color);
        doc.roundedRect(M, cursor, 5, 28, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12.5);
        doc.setTextColor(...INK);
        doc.text(label, M + 16, cursor + 18);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...MUTED);
        doc.text(right, pageW - M - 12, cursor + 18, { align: "right" });
        cursor += 38;
      };

      /* ---- summary cards (alert use cases) ---- */
      if (chosen.length) {
        let y = BAND_H + 34;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...INK);
        doc.text("Summary", M, y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...MUTED);
        doc.text(`${totalRecords} records in total`, pageW - M, y, { align: "right" });
        y += 14;

        const perRow = opts.orientation === "portrait" ? 3 : 4;
        const gap = 12;
        const cardW = (pageW - 2 * M - gap * (perRow - 1)) / perRow;
        const cardH = 54;
        chosen.forEach((u, i) => {
          const cx = M + (i % perRow) * (cardW + gap);
          const cy = y + Math.floor(i / perRow) * (cardH + gap);
          doc.setFillColor(...hex2rgb(u.soft));
          doc.roundedRect(cx, cy, cardW, cardH, 6, 6, "F");
          doc.setFillColor(...hex2rgb(u.color));
          doc.roundedRect(cx, cy, 5, cardH, 2, 2, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(20);
          doc.setTextColor(...INK);
          doc.text(String((rep[u.key] || []).length), cx + 18, cy + 28);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(...MUTED);
          doc.text(u.label, cx + 18, cy + 44, { maxWidth: cardW - 24 });
        });
        const cardRows = Math.ceil(chosen.length / perRow);
        cursor = y + cardRows * (cardH + gap) + 14;
      }

      /* ---- staff presence (cashier + writer) ---- */
      if (includePresence) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(...MUTED);
        doc.text(
          `Business day ${fmtDM(pres.window.from)} to ${fmtDM(pres.window.to)}. Minutes with no camera data are not counted as absent.`,
          M,
          cursor,
          { maxWidth: pageW - 2 * M }
        );
        cursor += 18;

        pres.stores.forEach((st) => {
          ensureSpace(150);
          band(`Staff presence - ${st.store}`, prettyDate(opts.date), [42, 159, 214], [227, 243, 251]);

          const roles = [];
          if (opts.cashier) roles.push(["Cashier", st.cashier]);
          if (opts.writer) roles.push(["Writer", st.writer]);

          const rows = [];
          const rowFor = (staff, period, timing, o) => [
            staff,
            period,
            timing,
            minsText(o.present),
            minsText(o.absent),
            minsText(o.longestAbsent),
            fmtHM(o.firstSeen),
            fmtHM(o.lastSeen),
          ];
          roles.forEach(([nm, r]) => {
            if (!r) return;
            if (opts.shiftWise) {
              (r.shifts || []).forEach((s) => rows.push(rowFor(nm, s.label, `${s.start} - ${s.end}`, s)));
            }
            rows.push(rowFor(nm, "Full day", `${fmtHM(pres.window.from)} - ${fmtHM(pres.window.to)}`, r.day));
          });

          autoTable(doc, {
            startY: cursor,
            head: [["Staff", "Period", "Timing", "Present", "Absent", "Longest absence", "First present", "Last present"]],
            body: rows,
            theme: "plain",
            margin: { left: M, right: M, top: 50, bottom: 44 },
            styles: {
              font: "helvetica",
              fontSize: opts.orientation === "portrait" ? 8 : 9,
              cellPadding: { top: 5, bottom: 5, left: 6, right: 6 },
              textColor: INK,
              lineColor: LINE,
              lineWidth: { bottom: 0.5 },
            },
            headStyles: {
              fillColor: [227, 243, 251],
              textColor: INK,
              fontStyle: "bold",
              lineColor: [42, 159, 214],
              lineWidth: { bottom: 1.2 },
            },
            alternateRowStyles: { fillColor: [250, 251, 255] },
            didParseCell: (h) => {
              if (h.section !== "body") return;
              if (h.row.raw[1] === "Full day") h.cell.styles.fontStyle = "bold";
              if (h.column.index === 3) h.cell.styles.textColor = TONE_RGB.ok;
              if (h.column.index === 4) h.cell.styles.textColor = TONE_RGB.bad;
            },
          });
          cursor = doc.lastAutoTable.finalY + 24;
        });
      }

      /* ---- one section per alert use case ---- */
      chosen.forEach((u) => {
        const rows = rep[u.key] || [];
        const ucCols = u.columns;
        const color = hex2rgb(u.color);
        const soft = hex2rgb(u.soft);

        ensureSpace(130);
        band(u.label, `${rows.length} record${rows.length === 1 ? "" : "s"}`, color, soft);
        const startY = cursor;

        if (!rows.length) {
          doc.setFontSize(10);
          doc.setTextColor(...MUTED);
          doc.text("No records for this date.", M + 4, startY + 8);
          cursor = startY + 32;
          return;
        }

        const t = (r) => new Date(r.postgresCreatedAt).getTime() || 0;
        const sorted = [...rows].sort((a, b) => (opts.order === "newest" ? t(b) - t(a) : t(a) - t(b)));
        const withImg = (r) => u.images(r).filter((i) => i.url);
        const imgLabel = (r) => {
          const imgs = withImg(r);
          if (!imgs.length) return "-";
          return imgs.length === 1 ? "View image" : imgs.map((x) => `View ${x.label}`).join(" | ");
        };
        const imgCol = withImages ? ucCols.length + 1 : -1;

        autoTable(doc, {
          startY,
          head: [["#", ...ucCols.map((c) => c.h), ...(withImages ? ["Image"] : [])]],
          body: sorted.map((r, i) => [
            String(i + 1),
            ...ucCols.map((c) => c.v(r)),
            ...(withImages ? [imgLabel(r)] : []),
          ]),
          theme: "plain",
          margin: { left: M, right: M, top: 50, bottom: 44 },
          styles: {
            font: "helvetica",
            fontSize: 8.5,
            cellPadding: { top: 5, bottom: 5, left: 6, right: 6 },
            textColor: INK,
            lineColor: LINE,
            lineWidth: { bottom: 0.5 },
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: soft,
            textColor: INK,
            fontStyle: "bold",
            lineColor: color,
            lineWidth: { bottom: 1.2 },
          },
          alternateRowStyles: { fillColor: [250, 251, 255] },
          columnStyles: { 0: { cellWidth: 28, textColor: MUTED } },
          didParseCell: (h) => {
            if (h.section !== "body") return;
            const ci = h.column.index;
            const r = sorted[h.row.index];
            if (ci >= 1 && ci <= ucCols.length) {
              const c = ucCols[ci - 1];
              if (c.tone) {
                h.cell.styles.textColor = TONE_RGB[c.tone(r)] || INK;
                h.cell.styles.fontStyle = "bold";
              }
            } else if (ci === imgCol && withImg(r).length) {
              h.cell.styles.textColor = [37, 99, 235];
            }
          },
          didDrawCell: (h) => {
            // make the "Image" cell clickable
            if (h.section === "body" && h.column.index === imgCol) {
              const imgs = withImg(sorted[h.row.index]);
              if (imgs.length) {
                const w = h.cell.width / imgs.length;
                imgs.forEach((im, i) =>
                  doc.link(h.cell.x + w * i, h.cell.y, w, h.cell.height, { url: im.url })
                );
              }
            }
          },
        });
        cursor = doc.lastAutoTable.finalY + 28;
      });

      /* ---- running header (continuation pages) and footer on every page ---- */
      const pages = doc.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        if (i > 1) {
          doc.setFillColor(238, 244, 255);
          doc.rect(0, 0, pageW, 38, "F");
          doc.setFillColor(61, 99, 221);
          doc.rect(0, 38, pageW, 1.5, "F");
          doc.setFontSize(9);
          doc.setTextColor(...MUTED);
          doc.text(`Empire Report  |  ${opts.store}  |  ${prettyDate(opts.date)}`, M, 24);
        }
        doc.setDrawColor(...LINE);
        doc.line(M, pageH - 30, pageW - M, pageH - 30);
        doc.setFontSize(8.5);
        doc.setTextColor(150, 158, 178);
        doc.text(`Generated on ${new Date().toLocaleString("en-IN")}`, M, pageH - 16);
        doc.text(`Page ${i} of ${pages}`, pageW - M, pageH - 16, { align: "right" });
      }

      const safeStore = String(opts.store).replace(/[^a-z0-9]+/gi, "_");
      doc.save(`${safeStore}_empire_report_${opts.date}.pdf`);
      setPdfOpen(false);
    } catch (e) {
      setPdfError(e?.response?.data?.message || e.message || "Could not create the PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  /* --------------------------------- UI ----------------------------------- */
  const isToday = date === maxDate;
  const presenceStores = report?.presence?.stores || [];

  return (
    <div className="rp-root">
      <style>{CSS}</style>

      <div className="rp-wrap">
        {/* toolbar */}
        <div className="rp-bar">
          <div className="sum">
            <b>{storeName} report</b>
            <span>
              {isToday ? "Today" : prettyDate(date)} ·{" "}
              {loading
                ? "Loading..."
                : `${total} alert records, ${presenceStores.length} store${presenceStores.length === 1 ? "" : "s"} with presence data`}
            </span>
          </div>

          <div className="rp-actions">
            <div className="rp-field">
              <label htmlFor="rp-date">Date</label>
              <input
                id="rp-date"
                type="date"
                value={date}
                max={maxDate}
                onChange={(e) => e.target.value && setDate(e.target.value)}
              />
            </div>
            {!isToday && (
              <button className="rp-btn" onClick={() => setDate(maxDate)}>
                Today
              </button>
            )}
            <button className="rp-btn" onClick={load} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button className="rp-btn primary" onClick={openDownload}>
              Download PDF
            </button>
          </div>
        </div>

        {error && (
          <div className="rp-error" role="alert">
            <span>{error}</span>
            <button className="rp-btn sm" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {/* view switch */}
        <div className="rp-seg" role="tablist" aria-label="Choose a view">
          <button role="tab" aria-selected={view === "presence"} className={view === "presence" ? "on" : ""} onClick={() => setView("presence")}>
            Staff presence
          </button>
          <button role="tab" aria-selected={view === "alerts"} className={view === "alerts" ? "on" : ""} onClick={() => setView("alerts")}>
            Alerts & logs{!loading && report ? ` (${total})` : ""}
          </button>
        </div>

        {loading && !report ? (
          <div className="rp-state">
            <div className="rp-spin" />
            Loading report for {prettyDate(date)}...
          </div>
        ) : !report && !error ? (
          <div className="rp-state">Pick a date to see the report.</div>
        ) : report && view === "presence" ? (
          <PresenceView
            presence={report.presence}
            date={date}
            selected={presenceStore}
            onSelect={setPresenceStore}
            loading={loading}
          />
        ) : report ? (
          <>
            {/* use case cards */}
            <div className="rp-ucs" role="tablist" aria-label="Choose a use case">
              {USE_CASES.map((u) => (
                <button
                  key={u.key}
                  role="tab"
                  aria-selected={active === u.key}
                  className={`rp-uc ${active === u.key ? "active" : ""}`}
                  style={{ "--c": u.color, "--s": u.soft }}
                  onClick={() => {
                    setActive(u.key);
                    setSearch("");
                  }}
                >
                  <span className="nm">{u.label}</span>
                  <span className="ct">{loading ? "–" : counts[u.key] ?? 0}</span>
                </button>
              ))}
            </div>

            <section className="rp-panel" style={{ "--c": uc.color, "--s": uc.soft }}>
              <div className="rp-panel-head">
                <div>
                  <h2>
                    <i />
                    {uc.label}
                  </h2>
                  <small>
                    {allRows.length} record{allRows.length === 1 ? "" : "s"} on {prettyDate(date)}
                    {(uc.key === "cashier" || uc.key === "writer") && " (business day 11:00 to 02:30)"}
                    {search && ` · ${filtered.length} match your search`}
                  </small>
                </div>
                <div className="rp-tools">
                  <div className="rp-search">
                    <input
                      type="search"
                      placeholder={`Search in ${uc.label}`}
                      aria-label={`Search in ${uc.label}`}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <button className="rp-btn sm" onClick={() => setNewestFirst((v) => !v)}>
                    {newestFirst ? "Newest first" : "Oldest first"}
                  </button>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="rp-empty">
                  {allRows.length === 0
                    ? `No ${uc.label.toLowerCase()} records on ${prettyDate(date)}.`
                    : "Nothing matches your search. Try a different word."}
                </div>
              ) : (
                <>
                  <div className="rp-tablewrap">
                    <table className="rp-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {cols.map((c) => (
                            <th key={c.h}>{c.h}</th>
                          ))}
                          <th>Image</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r, i) => {
                          const imgs = uc.images(r).filter((im) => im.url);
                          return (
                            <tr key={r._id || from + i}>
                              <td className="idx">{from + i + 1}</td>
                              {cols.map((c) => {
                                const text = c.v(r);
                                const tone = c.tone ? c.tone(r) : null;
                                return (
                                  <td key={c.h}>
                                    {tone ? <span className={`rp-badge ${tone}`}>{text}</span> : text}
                                  </td>
                                );
                              })}
                              <td>
                                {imgs.length ? (
                                  <div className="rp-thumbs">
                                    {imgs.map((im, k) => (
                                      <button
                                        key={im.label}
                                        className="rp-thumb"
                                        title={`View ${im.label}`}
                                        onClick={() => setPreview({ title: uc.label, items: imgs, index: k })}
                                      >
                                        <img src={im.url} alt={im.label} loading="lazy" />
                                        <span>{im.label}</span>
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* pagination */}
                  <div className="rp-foot">
                    <span className="info">
                      Showing {from + 1}–{Math.min(from + pageSize, filtered.length)} of {filtered.length}
                    </span>
                    <div className="right">
                      <label className="rp-size">
                        Rows
                        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                          {PAGE_SIZES.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                      <nav className="rp-pages" aria-label="Pagination">
                        <button className="rp-pg" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>
                          Prev
                        </button>
                        {pageList(safePage, totalPages).map((p, i) =>
                          p === "…" ? (
                            <span key={`d${i}`} className="rp-dots">
                              …
                            </span>
                          ) : (
                            <button
                              key={p}
                              className={`rp-pg ${p === safePage ? "on" : ""}`}
                              aria-current={p === safePage ? "page" : undefined}
                              onClick={() => setPage(p)}
                            >
                              {p}
                            </button>
                          )
                        )}
                        <button className="rp-pg" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        ) : null}
      </div>

      {/* DOWNLOAD DIALOG */}
      {pdfOpen && (
        <div className="rp-overlay" onClick={() => !pdfBusy && setPdfOpen(false)}>
          <div className="rp-modal rp-dl" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Download PDF report</h3>
            <p>Choose what you want in the PDF.</p>

            <div className="rp-presets">
              <button className="rp-btn sm" onClick={() => preset("all")}>Everything</button>
              <button className="rp-btn sm" onClick={() => preset("presence")}>Presence only</button>
              <button className="rp-btn sm" onClick={() => preset("alerts")}>Alerts only</button>
            </div>

            <div className="rp-two">
              <div className="rp-row">
                <label htmlFor="rp-pdf-date">Report date</label>
                <input
                  id="rp-pdf-date"
                  type="date"
                  value={opts.date}
                  max={maxDate}
                  onChange={(e) => setOpt({ date: e.target.value })}
                />
              </div>
              <div className="rp-row">
                <label htmlFor="rp-pdf-store">Store</label>
                <select
                  id="rp-pdf-store"
                  value={opts.store}
                  disabled={storeChoices.length < 2}
                  onChange={(e) => setOpt({ store: e.target.value })}
                >
                  {storeChoices.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* staff presence */}
            <div className="rp-group">
              <h4>
                <label className="rp-check" style={{ padding: 0 }}>
                  <input type="checkbox" checked={opts.presence} onChange={(e) => setOpt({ presence: e.target.checked })} />
                  Staff presence
                </label>
              </h4>
              <div className="rp-checks">
                <label className={`rp-check sub ${opts.presence ? "" : "off"}`}>
                  <input
                    type="checkbox"
                    disabled={!opts.presence}
                    checked={opts.cashier}
                    onChange={(e) => setOpt({ cashier: e.target.checked })}
                  />
                  Cashier
                </label>
                <label className={`rp-check sub ${opts.presence ? "" : "off"}`}>
                  <input
                    type="checkbox"
                    disabled={!opts.presence}
                    checked={opts.writer}
                    onChange={(e) => setOpt({ writer: e.target.checked })}
                  />
                  Writer
                </label>
                <label className={`rp-check sub ${opts.presence ? "" : "off"}`}>
                  <input
                    type="checkbox"
                    disabled={!opts.presence}
                    checked={opts.shiftWise}
                    onChange={(e) => setOpt({ shiftWise: e.target.checked })}
                  />
                  Shift wise rows
                </label>
              </div>
            </div>

            {/* alerts */}
            <div className="rp-group">
              <h4>
                <span>Alerts & logs</span>
                <span>
                  <button className="rp-btn sm" onClick={() => setAllCases(true)} style={{ marginRight: 6 }}>
                    Select all
                  </button>
                  <button className="rp-btn sm" onClick={() => setAllCases(false)}>
                    Clear
                  </button>
                </span>
              </h4>
              <div className="rp-checks">
                {USE_CASES.map((u) => (
                  <label className="rp-check" key={u.key}>
                    <input type="checkbox" checked={!!opts.cases[u.key]} onChange={(e) => setCase(u.key, e.target.checked)} />
                    <i style={{ background: u.color }} />
                    {u.label}
                    {report && opts.date === date && opts.store === storeName ? ` (${counts[u.key] ?? 0})` : ""}
                  </label>
                ))}
              </div>
            </div>

            {/* layout */}
            <div className="rp-group">
              <h4>Layout</h4>
              <div className="rp-two">
                <div className="rp-row" style={{ marginBottom: 6 }}>
                  <label htmlFor="rp-pdf-order">Order of alert rows</label>
                  <select id="rp-pdf-order" value={opts.order} onChange={(e) => setOpt({ order: e.target.value })}>
                    <option value="oldest">Oldest first</option>
                    <option value="newest">Newest first</option>
                  </select>
                </div>
                <div className="rp-row" style={{ marginBottom: 6 }}>
                  <label htmlFor="rp-pdf-orient">Page</label>
                  <select id="rp-pdf-orient" value={opts.orientation} onChange={(e) => setOpt({ orientation: e.target.value })}>
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
              </div>
              <label className="rp-check">
                <input type="checkbox" checked={opts.images} onChange={(e) => setOpt({ images: e.target.checked })} />
                Include image links in alert tables
              </label>
            </div>

            <div className="rp-sum">
              {nothingSelected
                ? "Nothing selected yet."
                : `${presenceOn ? "Staff presence" : ""}${presenceOn && selectedCases.length ? " + " : ""}${
                    selectedCases.length ? `${selectedCases.length} alert type${selectedCases.length === 1 ? "" : "s"}` : ""
                  } · ${prettyDate(opts.date)} · ${opts.store}`}
            </div>

            {pdfError && (
              <div className="rp-error" role="alert" style={{ margin: "12px 0 0" }}>
                {pdfError}
              </div>
            )}

            <div className="rp-modal-actions">
              <button className="rp-btn" onClick={() => setPdfOpen(false)} disabled={pdfBusy}>
                Cancel
              </button>
              <button className="rp-btn primary" onClick={downloadPdf} disabled={pdfBusy || nothingSelected}>
                {pdfBusy ? "Preparing PDF..." : "Download PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* image preview */}
      {preview && (
        <div className="rp-overlay" onClick={() => setPreview(null)}>
          <div className="rp-modal rp-preview" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="bar">
              <span>
                {preview.title} · {preview.items[preview.index].label}
              </span>
              <div className="nav">
                {preview.items.length > 1 &&
                  preview.items.map((im, k) => (
                    <button
                      key={im.label}
                      className={`rp-btn sm ${k === preview.index ? "primary" : ""}`}
                      onClick={() => setPreview({ ...preview, index: k })}
                    >
                      {im.label}
                    </button>
                  ))}
                <button className="rp-btn sm" onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>
            <img src={preview.items[preview.index].url} alt={preview.items[preview.index].label} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpireReport;